// SPDX-License-Identifier: Apache-2.0

// Settings (Hestia).
//
// Four tabs: Backends · Vault · Audit · Setup.
//   - Backends mounts Talos's <ZeroTVMCard /> via dynamic import + an
//     `experimentalBackends` toggle; lists installed backends with status dots.
//   - Vault gives a folder grant + reconnect button (FSAccess gotcha).
//   - Audit mounts <AuditLog />.
//   - Setup shows the one-liner and the uninstall instruction.

import {
  Show,
  Suspense,
  createSignal,
  createMemo,
  createResource,
  lazy,
  type Component,
} from "solid-js";
import {
  fsAccessSupported,
  getVaultDir,
  reconnectVaultDir,
  requestVaultDir,
  revokeVaultDir,
} from "@tether/storage";
import AuditLog from "../components/AuditLog";
import IntegrationsSetupWizard, {
  type Flow as CommsFlow,
} from "../components/integrations/IntegrationsSetupWizard";
import TestConnectionButton from "../components/integrations/TestConnectionButton";

// Talos's Zero-TVM showcase card. Dynamic import so it doesn't pull WebGPU
// kernels into the Settings bundle for users who never enable it. The package
// may not export the symbol yet — render a one-line "coming soon" if so.
const Placeholder: Component = () => (
  <div class="hestia-settings__placeholder">
    Zero-TVM card not wired yet — Talos ships it in the Veil stream.
  </div>
);

const ZeroTVMCard = lazy<Component>(async () => {
  try {
    const mod = (await import("@tether/veil")) as Record<string, unknown>;
    const ctor = (mod.ZeroTVMCard ?? mod.default) as Component | undefined;
    if (typeof ctor === "function") {
      return { default: ctor };
    }
  } catch {
    /* fall through */
  }
  return { default: Placeholder };
});

type Tab = "backends" | "vault" | "comms" | "audit" | "setup";

const TABS: ReadonlyArray<{ id: Tab; label: string }> = [
  { id: "backends", label: "backends" },
  { id: "vault", label: "vault" },
  { id: "comms", label: "comms" },
  { id: "audit", label: "audit" },
  { id: "setup", label: "setup" },
];

const MCP_BASE = "http://localhost:5417/api/integrations";

interface IntegrationsStatus {
  slack: { configured: boolean; tool_count: number; team?: string };
  gmail: { configured: boolean; tool_count: number; email?: string };
  next_steps: string[];
}

type BackendStatus = "running" | "unreachable" | "experimental";
interface BackendRow {
  id: string;
  label: string;
  tier: string;
  status: BackendStatus;
  description: string;
}

const KNOWN_BACKENDS: BackendRow[] = [
  {
    id: "webllm",
    label: "WebLLM (in-tab)",
    tier: "internal",
    status: "running",
    description: "browser-local Llama on WebGPU; nothing leaves the tab.",
  },
  {
    id: "anthropic",
    label: "Anthropic",
    tier: "public",
    status: "running",
    description: "claude api for non-private prompts.",
  },
  {
    id: "zero-tvm",
    label: "Zero-TVM",
    tier: "secret",
    status: "experimental",
    description: "showcase backend, opt-in via experimentalBackends.",
  },
];

function StatusDot(props: { status: BackendStatus }) {
  const cls = createMemo(() => `hestia-settings__dot hestia-settings__dot--${props.status}`);
  return <span class={cls()} aria-label={props.status} />;
}

function SetupLine() {
  const ua =
    typeof navigator !== "undefined"
      ? (navigator.userAgent || "").toLowerCase()
      : "";
  const isWin = ua.includes("windows");
  return isWin ? "irm tether.dev/setup.ps1 | iex" : "curl tether.dev/setup | bash";
}

export default function Settings() {
  const [tab, setTab] = createSignal<Tab>("backends");
  const [experimentalBackends, setExperimentalBackends] = createSignal(false);

  // Iris setup wizard — modal launched from the Comms tab cards.
  const [wizardOpen, setWizardOpen] = createSignal(false);
  const [wizardFlow, setWizardFlow] = createSignal<CommsFlow>("slack");

  // /api/integrations/status — refetched every time the user opens the
  // Comms tab AND after the wizard fires onFlowComplete.
  const [integrationsStatus, { refetch: refetchIntegrationsStatus }] = createResource<
    IntegrationsStatus | null
  >(async () => {
    try {
      const res = await fetch(`${MCP_BASE}/status`);
      if (!res.ok) return null;
      return (await res.json()) as IntegrationsStatus;
    } catch {
      return null;
    }
  });

  const openWizard = (flow: CommsFlow) => {
    setWizardFlow(flow);
    setWizardOpen(true);
  };
  const [defaultPerTier, setDefaultPerTier] = createSignal<Record<string, string>>({
    public: "anthropic",
    internal: "webllm",
    secret: "webllm",
  });

  // Vault state
  const [vaultHandle, { refetch: refetchVault }] = createResource(async () => {
    if (!fsAccessSupported()) return null;
    return await getVaultDir({ mode: "readwrite" });
  });
  const [vaultError, setVaultError] = createSignal<string | null>(null);

  const onPick = async () => {
    setVaultError(null);
    try {
      await requestVaultDir({ mode: "readwrite" });
      await refetchVault();
    } catch (e) {
      setVaultError((e as Error).message);
    }
  };
  const onReconnect = async () => {
    setVaultError(null);
    try {
      const r = await reconnectVaultDir({ mode: "readwrite" });
      if (!r) setVaultError("permission was not granted");
      await refetchVault();
    } catch (e) {
      setVaultError((e as Error).message);
    }
  };
  const onRevoke = async () => {
    await revokeVaultDir();
    await refetchVault();
  };

  // Backends derived list — hides "experimental" rows when the toggle is off.
  const backends = createMemo<BackendRow[]>(() =>
    experimentalBackends()
      ? KNOWN_BACKENDS
      : KNOWN_BACKENDS.filter((b) => b.status !== "experimental"),
  );

  const setDefaultFor = (tier: string, id: string) => {
    setDefaultPerTier({ ...defaultPerTier(), [tier]: id });
  };

  return (
    <div class="hestia-settings">
      <header class="hestia-settings__head">
        <div class="hestia-settings__eyebrow">settings</div>
        <h1 class="hestia-settings__title">The hearth and its house.</h1>
      </header>

      <nav class="hestia-settings__tabs" role="tablist">
        {TABS.map((t) => (
          <button
            type="button"
            role="tab"
            aria-selected={tab() === t.id}
            class="hestia-settings__tab"
            classList={{ active: tab() === t.id }}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </nav>

      <Show when={tab() === "backends"}>
        <section class="hestia-settings__panel" role="tabpanel">
          <label class="hestia-settings__toggle">
            <input
              type="checkbox"
              checked={experimentalBackends()}
              onChange={(e) => setExperimentalBackends(e.currentTarget.checked)}
            />
            <span>show experimental backends</span>
          </label>

          <div class="hestia-settings__backends">
            {backends().map((b) => (
              <div class="hestia-settings__backend">
                <div class="hestia-settings__backend-row">
                  <StatusDot status={b.status} />
                  <span class="hestia-settings__backend-label">{b.label}</span>
                  <span class="hestia-settings__backend-tier">{b.tier}</span>
                </div>
                <div class="hestia-settings__backend-desc">{b.description}</div>
                <div class="hestia-settings__backend-default">
                  <span class="hestia-settings__hint">default for {b.tier}:</span>
                  <select
                    value={defaultPerTier()[b.tier] ?? b.id}
                    onChange={(e) => setDefaultFor(b.tier, e.currentTarget.value)}
                  >
                    {KNOWN_BACKENDS.filter((x) => x.tier === b.tier).map((x) => (
                      <option value={x.id}>{x.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            ))}
          </div>

          <Show when={experimentalBackends()}>
            <div class="hestia-settings__zerotvm">
              <Suspense fallback={<div class="hestia-settings__placeholder">loading Zero-TVM card…</div>}>
                <ZeroTVMCard />
              </Suspense>
            </div>
          </Show>
        </section>
      </Show>

      <Show when={tab() === "vault"}>
        <section class="hestia-settings__panel" role="tabpanel">
          <Show
            when={fsAccessSupported()}
            fallback={
              <div class="hestia-settings__placeholder">
                file system access api not available — chrome/edge 86+ required.
              </div>
            }
          >
            <div class="hestia-settings__vault">
              <div class="hestia-settings__vault-row">
                <span class="hestia-settings__hint">current grant:</span>
                <code>{vaultHandle()?.name ?? "(none)"}</code>
              </div>
              <p class="hestia-settings__copy">
                chrome resets folder permission to "prompt" on every fresh tab.
                reconnect from a click — the os won't let me ask without one.
              </p>
              <div class="hestia-settings__row">
                <button type="button" class="hestia-settings__primary" onClick={() => void onPick()}>
                  pick folder
                </button>
                <button type="button" class="hestia-settings__ghost" onClick={() => void onReconnect()}>
                  reconnect vault
                </button>
                <button type="button" class="hestia-settings__ghost" onClick={() => void onRevoke()}>
                  forget grant
                </button>
              </div>
              <Show when={vaultError()}>
                <div class="hestia-settings__error">{vaultError()}</div>
              </Show>
            </div>
          </Show>
        </section>
      </Show>

      <Show when={tab() === "comms"}>
        <section class="hestia-settings__panel" role="tabpanel" data-testid="comms-tab-panel">
          <p class="hestia-settings__copy">
            plug in slack and gmail. tokens stay on your machine —{" "}
            <code>~/.tether-brain/integrations.json</code> chmod 0600,
            never to localStorage, never to the cloud.
          </p>
          <div class="iris-comms-cards">
            <div class="iris-comms-card" data-testid="iris-comms-card-slack">
              <span class="iris-comms-card__glyph iris-comms-card__glyph--slack">#</span>
              <div class="iris-comms-card__body">
                <h3 class="iris-comms-card__title">Slack</h3>
                <span class="iris-comms-card__status">
                  <span
                    class="iris-comms-card__dot"
                    classList={{
                      "iris-comms-card__dot--ok":
                        integrationsStatus()?.slack.configured ?? false,
                    }}
                  />
                  <Show
                    when={integrationsStatus()?.slack.configured}
                    fallback={<span>not configured</span>}
                  >
                    <span>
                      configured · {integrationsStatus()?.slack.tool_count ?? 0}{" "}
                      slack.* tools
                      <Show when={integrationsStatus()?.slack.team}>
                        {" "}
                        · workspace{" "}
                        <code>{integrationsStatus()?.slack.team}</code>
                      </Show>
                    </span>
                  </Show>
                </span>
              </div>
              <div class="iris-comms-card__action">
                <button
                  type="button"
                  class="hestia-settings__primary"
                  data-testid="iris-comms-setup-slack"
                  onClick={() => openWizard("slack")}
                >
                  {integrationsStatus()?.slack.configured ? "reconfigure" : "setup"}
                </button>
                <Show when={integrationsStatus()?.slack.configured}>
                  <TestConnectionButton which="slack" />
                </Show>
              </div>
            </div>

            <div class="iris-comms-card" data-testid="iris-comms-card-gmail">
              <span class="iris-comms-card__glyph iris-comms-card__glyph--gmail">@</span>
              <div class="iris-comms-card__body">
                <h3 class="iris-comms-card__title">Gmail</h3>
                <span class="iris-comms-card__status">
                  <span
                    class="iris-comms-card__dot"
                    classList={{
                      "iris-comms-card__dot--ok":
                        integrationsStatus()?.gmail.configured ?? false,
                    }}
                  />
                  <Show
                    when={integrationsStatus()?.gmail.configured}
                    fallback={<span>not configured</span>}
                  >
                    <span>
                      configured · {integrationsStatus()?.gmail.tool_count ?? 0}{" "}
                      mail.* tools
                      <Show when={integrationsStatus()?.gmail.email}>
                        {" "}
                        · <code>{integrationsStatus()?.gmail.email}</code>
                      </Show>
                    </span>
                  </Show>
                </span>
              </div>
              <div class="iris-comms-card__action">
                <button
                  type="button"
                  class="hestia-settings__primary"
                  data-testid="iris-comms-setup-gmail"
                  onClick={() => openWizard("gmail")}
                >
                  {integrationsStatus()?.gmail.configured ? "reconfigure" : "setup"}
                </button>
                <Show when={integrationsStatus()?.gmail.configured}>
                  <TestConnectionButton which="gmail" />
                </Show>
              </div>
            </div>
          </div>
        </section>
      </Show>

      <Show when={tab() === "audit"}>
        <section class="hestia-settings__panel" role="tabpanel">
          <AuditLog />
        </section>
      </Show>

      <IntegrationsSetupWizard
        open={wizardOpen()}
        initialFlow={wizardFlow()}
        onClose={() => setWizardOpen(false)}
        onFlowComplete={() => void refetchIntegrationsStatus()}
      />

      <Show when={tab() === "setup"}>
        <section class="hestia-settings__panel" role="tabpanel">
          <p class="hestia-settings__copy">
            One line installs the local server so the brain auto-starts at login.
            <br />
            installs to <code>~/.tether-brain/</code> — never under <code>~/Documents/</code>.
          </p>
          <pre class="hestia-settings__cmd"><code>{SetupLine()}</code></pre>
          <p class="hestia-settings__copy">
            uninstall (keeps your audit log):
          </p>
          <pre class="hestia-settings__cmd">
            <code>curl tether.dev/setup | bash -s -- --uninstall</code>
          </pre>
          <p class="hestia-settings__hint">
            paths it touches:
          </p>
          <ul class="hestia-settings__paths">
            <li>
              <code>~/.tether-brain/bin/tether-brain</code> — the binary
            </li>
            <li>
              <code>~/Library/LaunchAgents/com.tether.brain.plist</code> — macOS auto-start
            </li>
            <li>
              <code>~/.config/systemd/user/tether-brain.service</code> — Linux auto-start
            </li>
            <li>
              <code>~/.tether-brain/audit.log</code> — every read/write/send
            </li>
            <li>
              <code>~/.tether-brain/setup.log</code> — what the installer did
            </li>
          </ul>
        </section>
      </Show>
    </div>
  );
}
